import { apiClient } from './client';

const AI_REPORTING_REQUEST_TIMEOUT_MS = 600000;

export type AiReportStatus = 'success' | 'clarification_required' | 'unsupported' | 'error';
export type AiVisualizationType = 'table' | 'kpi' | 'bar' | 'line' | 'pie' | 'dashboard';

export interface AiReportColumn {
  key?: string;
  field?: string;
  label: string;
  dataType?: string;
  currency?: string;
  fractionDigits?: number;
  exportAllowed?: boolean;
  providerExposure?: 'aggregate' | 'calendar' | 'restricted';
}

export interface AiReportVisualization {
  type: AiVisualizationType;
  x?: string;
  y?: string;
}

export type AiReportRow = Record<string, unknown>;

export interface AiReportMetadata {
  metricLabel: string;
  groupedBy: string;
  periodLabel: string;
  scopeLabel?: string;
  currency?: string;
  sourceSync?: Array<{ companyId: number; lastSyncAt: string | null; status: string }>;
}

export interface AiReportKpi {
  label: string;
  value: unknown;
  dataType?: string;
  hint?: string;
  currency?: string;
  fractionDigits?: number;
}

export interface AiReportGrid {
  columns: AiReportColumn[];
  rows: AiReportRow[];
  totals?: Record<string, number>;
  matchingRowCount?: number;
  truncated?: boolean;
  fullTotals?: Record<string, number | null>;
  periodTotals?: Record<string, Record<string, number | null>>;
  metricAggregations?: Record<string, string>;
}

export interface AiReportChart {
  enabled: boolean;
  type: 'bar' | 'line' | 'pie' | 'kpi' | 'none';
  xField: string | null;
  yField: string | null;
  data: AiReportRow[];
  sourceRowIndexes?: number[];
}

export interface AiReportInterpretationMetric {
  metricId: string;
  label: string;
  dataType: string;
}

export interface AiReportInterpretationDimension {
  dimensionId: string;
  label: string;
}

export interface AiReportInterpretationTimeRange {
  type: string;
  startDate?: string | null;
  endDate?: string | null;
  fieldId?: string | null;
}

export interface AiReportInterpretation {
  datasetId: string;
  datasetLabel: string;
  mode?: string;
  analysisType?: string;
  metrics: AiReportInterpretationMetric[];
  dimensions: AiReportInterpretationDimension[];
  timeRange: AiReportInterpretationTimeRange | null;
  limit?: number;
  sort: Array<{
    metricId?: string;
    dimensionId?: string;
    columnId?: string;
    direction: 'asc' | 'desc';
  }>;
}

export interface AiReportWidget {
  widgetId: string;
  title: string;
  mode?: string;
  metadata?: AiReportMetadata;
  kpis?: AiReportKpi[];
  grid?: AiReportGrid;
  chart?: AiReportChart;
  visualization?: AiReportVisualization;
  columns: AiReportColumn[];
  rows: AiReportRow[];
  summary?: string | null;
  assumptions?: string[];
  interpretation?: AiReportInterpretation;
}

export interface AiReportResponse {
  requestId: string;
  status: AiReportStatus;
  title: string;
  queryKind: 'single_report' | 'dashboard' | 'clarification' | 'unsupported';
  mode?: string;
  metadata?: AiReportMetadata;
  kpis?: AiReportKpi[];
  grid?: AiReportGrid;
  chart?: AiReportChart;
  visualization?: AiReportVisualization;
  columns?: AiReportColumn[];
  rows?: AiReportRow[];
  summary?: string | null;
  assumptions?: string[];
  followUpQuestions?: string[];
  clarification?: string | null;
  unsupportedReason?: string | null;
  errorCode?: string | null;
  availableAlternatives?: string[];
  missingCapabilities?: string[];
  recommendedSchemaFix?: string | null;
  widgets?: AiReportWidget[];
  interpretation?: AiReportInterpretation;
  interaction?: {
    workRows?: Array<{ id: string; rowIndex: number; kind: 'customer' | 'expiry_batch'; label: string }>;
    sessionId: string;
    expiresAt: string;
    scope: { companyId?: number; branchIds?: string[] };
    choices: Array<{ id: string; label: string }>;
    drillRows: Array<{ id: string; rowIndex: number }>;
    editable: boolean;
    periodMode: 'fixed' | 'rolling' | 'current';
    period?: { preset?: string; startDate?: string; endDate?: string; fieldId?: string };
  };
  changes?: Array<{ field: string; before: string; after: string }>;
  reconciliation?: { status: 'matched'; parentTotals: Record<string, number | null> };
}

export interface AiReportEdit {
  metrics?: string[];
  dimensions?: string[];
  timeRange?: { preset: string; startDate?: string; endDate?: string; fieldId?: string };
  scope?: { companyId: number | null; branchIds: string[] };
}

export interface AiReportInteractionRequest {
  sessionId: string;
  type: 'choose' | 'edit' | 'drill';
  choiceId?: string;
  edit?: AiReportEdit;
  signal?: AbortSignal;
}

export interface AiReportRequest {
  signal?: AbortSignal;
  question: string;
  outputMode?: 'auto' | 'table' | 'chart';
  includeSummary?: boolean;
  companyId?: number;
  branchIds?: string[];
  previousRequestId?: string;
}

export interface AiMetricSourceHealth {
  channel: 'marg_operational' | 'marg_accounting' | 'untracked';
  state: 'within_expectation' | 'stale' | 'failed' | 'running' | 'unknown';
  expectationMinutes: number | null;
  checkedAt: string;
  companiesTruncated: boolean;
  companies: Array<{ companyId: number; state: AiMetricSourceHealth['state']; status: string; lastSyncAt: string | null; ageMinutes: number | null }>;
  explanation: string;
}
export interface AiTenantMetricDefinition {
  label: string;
  aliases: string[];
  note: string;
  ownerName: string | null;
  approverName: string | null;
  approvedAt: string | null;
  freshnessMinutes: number | null;
  sourceHealth: AiMetricSourceHealth;
}
export interface AiMetricDefinition extends AiTenantMetricDefinition {
  metricId: string;
  displayName: string;
  datasetId: string;
  source: string;
  dataType: string;
  currency: string | null;
  aggregation: string;
  businessRules: string[];
  contractHash: string;
  version: number;
  state: 'not_configured' | 'draft' | 'approved' | 'withdrawn' | 'needs_review' | 'owner_unavailable' | 'approver_unavailable';
  ownerUserId: string | null;
  ownerEligible: boolean;
  approvedBy: string | null;
  updatedAt: string | null;
}
export interface AiMetricDefinitionDetail extends AiMetricDefinition {
  owners: Array<{ id: string; name: string }>;
  ownersTruncated: boolean;
  events: Array<{ id: string; actor: string; createdAt: string; changes: { action: string; state: string; version: number; label: string; note: string; freshnessMinutes: number | null } }>;
}
export interface AiMetricDefinitionUpdate {
  action: 'save' | 'approve' | 'withdraw';
  expectedVersion: number;
  expectedContractHash: string;
  label?: string;
  aliases?: string[];
  note?: string;
  ownerUserId?: string | null;
  freshnessMinutes?: number | null;
}

export type AiWorkFlow = 'collections' | 'inventory' | 'sales' | 'management';
export type AiWorkState = 'planned' | 'in_progress' | 'completed' | 'cancelled';
export type AiWorkOutcome = 'follow_up_complete' | 'reviewed' | 'no_action_needed' | 'data_issue_found' | 'cancelled';
export interface AiWorkSource {
  insight?: { id: string; providerId: string; privateOnly: boolean };
  entity?: { kind: 'customer' | 'expiry_batch'; label: string; companyId: number };
  datasetId: string; dataset: string; measures: string[]; grouping: string[];
  timeRange: { preset?: string; startDate?: string; endDate?: string } | null;
  mode?: string;
  absenceRange?: { startDate: string; endDate: string } | null;
  comparison?: { type: string; period: string | null; startDate: string | null; endDate: string | null; changeDirection: string | null } | null;
  capturedAt: string; companyCount: number; branchCount: number;
}
export interface AiReportWorkItem {
  sourceKind?: 'report' | 'customer' | 'expiry_batch' | 'insight';
  id: string; workflow: AiWorkFlow; state: AiWorkState; version: number; title: string; note: string;
  creatorUserId: string; creatorName: string | null; assignedUserId: string | null; ownerName: string | null; ownerEligible: boolean;
  dueAt: string | null; outcome: AiWorkOutcome | null; outcomeNote: string; reportedAmount: string | null; currency: string;
  source: AiWorkSource; sourceDefinitionCurrent: boolean; canAssign: boolean; expiresAt: string; createdAt: string; updatedAt: string;
}
export interface AiReportWorkDetail extends AiReportWorkItem {
  assignees: Array<{ id: string; name: string }>; assigneesTruncated: boolean;
  events: Array<{ id: string; createdAt: string; actor: string; changes: { action: string; version: number; state: AiWorkState; note?: string; outcomeNote?: string; reportedAmount?: string | null; currency?: string } }>;
}
export interface AiReportWorkPreview {
  requestId: string; source: AiWorkSource; workflows: AiWorkFlow[]; expiresAt: string;
  assignees: Array<{ id: string; name: string }>; assigneesTruncated: boolean;
}
export interface AiReportWorkCreate {
  insightId?: string;
  entityChoice?: { sessionId: string; choiceId: string };
  requestId: string; idempotencyKey: string; workflow: AiWorkFlow; title: string; note: string; assignedUserId: string | null; dueAt: string | null;
}
export interface AiReportWorkUpdate {
  expectedVersion: number; state: AiWorkState; title: string; note: string; assignedUserId: string | null; dueAt: string | null;
  outcome: AiWorkOutcome | null; outcomeNote: string; reportedAmount: string | null; currentSourceReviewed?: boolean;
}
export interface AiReportEntityBatchResult {
  items: Array<{ choiceId: string; status: 'saved' | 'unconfirmed' | 'not_attempted'; workItemId?: string; statusCode?: number }>;
}

export interface AiCatalogMetadata {
  scopes?: {
    companies: Array<{ id: number; name: string }>;
    branches: Array<{ id: string; name: string; companyId?: number }>;
  };
  catalogVersion: string;
  feature?: {
    enabled: boolean;
    summariesEnabled: boolean;
    maxRows: number;
  };
  datasets: Array<{ datasetId: string; domain: string; grain: string; description: string }>;
  metrics: Array<{ metricId: string; displayName: string; datasetId: string; dataType?: string; synonyms?: string[]; aggregation?: string; businessRules?: string[]; tenantDefinition?: AiTenantMetricDefinition }>;
  dimensions: Array<{ dimensionId: string; displayName: string; datasetId: string; synonyms?: string[] }>;
  displayColumns?: Array<{ columnId: string; datasetId: string; label: string; dataType?: string; defaultForDetail?: boolean; synonyms?: string[] }>;
  reportTemplates: Array<{ templateId: string; displayName: string; analysisType?: string; synonyms?: string[]; datasetId?: string; measures?: string[]; description?: string; businessTerms?: string[] }>;
  dashboardTemplates: Array<{ dashboardId: string; displayName: string; description?: string; synonyms?: string[] }>;
  reportAreas?: string[];
  suggestedQuestions?: string[];
}

export interface AiReportHistoryItem {
  requestId: string;
  question: string;
  outputMode?: string | null;
  queryKind?: string | null;
  rowCount?: number | null;
  status: 'success' | 'error';
  errorCode?: string | null;
  errorMessage?: string | null;
  createdAt: string;
  restorable?: boolean;
  savedInterpretation?: { metricLabel: string; groupedBy: string; periodLabel: string; scopeLabel: string; periodMode: string };
}

export interface AiTenantUsageSummary {
  budget?: {
    periodStart: string;
    exposureTokens: number;
    exposureCostCents: number | null;
    inFlightAttempts: number;
    uncertainAttempts: number;
    unknownTokenAttempts: number;
    unknownCostAttempts: number;
  };
  periodStart: string;
  totalCalls: number;
  successfulCalls: number;
  failedCalls: number;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  estimatedCostCents: number | null;
  byModel: Array<{
    provider: string;
    model: string | null;
    calls: number;
    totalTokens: number;
    estimatedCostCents: number | null;
  }>;
}

export interface AiProviderSettings {
  configured: boolean;
  enabled: boolean;
  /** Super-admin granted BYOK mode: this workspace runs AI on its own key, unbilled. */
  byokEnabled: boolean;
  provider: string;
  model: string;
  summaryModel: string | null;
  apiKeyConfigured: boolean;
  apiKeyLast4: string | null;
  endpointUrl: string | null;
  organizationId: string | null;
  maxTokens: number | null;
  temperature: number | null;
  monthlyTokenLimit: number | null;
  monthlyCostLimitCents: number | null;
  inputTokenCostPer1mCents: number | null;
  outputTokenCostPer1mCents: number | null;
  timeoutMs: number;
  maxResultRows: number;
  maxSummaryRows: number;
  dailyUserCallLimit: number;
  dailyTenantCallLimit: number;
  monthlyCompanyCallLimit: number;
  maskSensitiveFields: boolean;
  summariesEnabled: boolean;
  ratePerUserPerMinute: number;
  ratePerTenantPerHour: number;
  maxConcurrentPerUser: number;
  maxConcurrentPerTenant: number;
  lastTestedAt: string | null;
  lastTestStatus: string | null;
  lastTestError: string | null;
  updatedAt: string | null;
  usage: AiTenantUsageSummary;
}

export interface AiProviderSettingsUpdate {
  enabled?: boolean;
  provider?: string;
  model?: string;
  summaryModel?: string | null;
  apiKey?: string | null;
  clearApiKey?: boolean;
  endpointUrl?: string | null;
  organizationId?: string | null;
  maxTokens?: number | null;
  temperature?: number | null;
  monthlyTokenLimit?: number | null;
  monthlyCostLimitCents?: number | null;
  inputTokenCostPer1mCents?: number | null;
  outputTokenCostPer1mCents?: number | null;
  timeoutMs?: number | null;
  maxResultRows?: number | null;
  maxSummaryRows?: number | null;
  dailyUserCallLimit?: number | null;
  dailyTenantCallLimit?: number | null;
  monthlyCompanyCallLimit?: number | null;
  maskSensitiveFields?: boolean | null;
  summariesEnabled?: boolean | null;
  ratePerUserPerMinute?: number | null;
  ratePerTenantPerHour?: number | null;
  maxConcurrentPerUser?: number | null;
  maxConcurrentPerTenant?: number | null;
}

export interface AiProviderTestResult {
  success: boolean;
  message: string;
  testedAt: string;
}

export interface AiSetupScope { companyId?: number; branchIds?: string[] }
export type AiReviewStatus = 'new' | 'in_review' | 'needs_data' | 'planned_fix' | 'resolved' | 'dismissed';
export interface AiReportActivity {
  scope: 'retained_workspace_server_records'; requestedDays: number; periodStart: string; checkedAt: string; coverage: string[];
  reports: { recorded: number; answered: number; clarification: number; unsupported: number; errors: number; cancelled: number; unclassified: number };
  adoption: { activeUsers: number; repeatUsers: number };
  clientRendering: { declaredReports: number; declaringUsers: number; workspaceReports: number; assistantReports: number; withoutDeclaration: number };
  setupParticipation: { checkingUsers: number; verifiedUsers: number; failedUsers: number };
  setupCohort: { checkingUsers: number; verifiedUsers: number; readyUsers: number; answeredUsers: number; helpfulUsers: number; answeredWithinFiveMinutes: number; medianSecondsToAnswer: number | null };
  widgets: { pinningUsers: number; sqlRefreshUsers: number; cacheReadUsers: number; activeUsers: number; repeatUsers: number };
  operations: Array<{ kind: string; count: number; answered: number }>;
  workflows: Array<{ kind: string; recorded: number; answered: number; activeUsers: number; repeatUsers: number; helpful: number; incorrect: number; unclear: number }>;
  profiles: { limit: number; omittedGroups: number; omittedRecords: number; unclassifiedRecords: number; groups: Array<{ profileKey: string; implementationSha256: string; catalogSha256: string; promptFingerprint: string; parseModelFingerprint: string | null; parseMode: 'no_parse_attempt' | 'unknown_parse_model' | 'observed_parse_model'; recorded: number; answered: number; clarification: number; unsupported: number; errorsOrCancelled: number; unclassified: number; helpful: number; incorrect: number; unclear: number }> };
  daily: Array<{ day: string; recorded: number; answered: number }>;
  feedback: { received: number; helpful: number; incorrect: number; unclear: number; open: number; resolved: number; dismissed: number };
  provider: { recordedAttempts: number; failedAttempts: number; missingUsage: number; missingPrice: number; recordedCostCents: string | null };
  providerCallTypes: Array<{ callType: string; count: number }>;
  unresolvedExposure: { reservations: number; costCents: string | null };
  persistence: { periodStartDay: string; periodEndDay: string; recordedFailures: string; kinds: Array<{ kind: string; failures: string }> };
  helpfulAnswerCost: { ratedAnswers: number; unknownCostAnswers: number; averageCostCents: string | null };
}
export type AiReviewCategory = 'definition' | 'data_freshness' | 'data_quality' | 'model_interpretation' | 'permissions' | 'usability' | 'other';
export interface AiReportReviewCase {
  caseId: string; requestId: string; rating: 'helpful' | 'incorrect' | 'unclear'; status: AiReviewStatus;
  category: AiReviewCategory | null; version: number; createdAt: string; updatedAt: string;
  reviewable: boolean; reportStatus: string; model: string | null; catalogVersion: string | null;
  catalogSha256: string | null; promptVersion: string | null; question?: string; comment?: string | null;
  assignedUserId?: string | null; dueAt?: string | null; resolutionNote?: string | null; reviewedAt?: string | null;
  detailsUnavailable?: string;
  interpretation?: { dataset: string; measures: string[]; companyCount: number; branchCount: number };
}
export interface AiReportReviewDetail extends AiReportReviewCase {
  events: Array<{ id: string; eventType: string; createdAt: string; actor: string; changes: { before?: { status: string }; after?: { status: string }; resolutionNote?: string; rating?: string } }>;
  assignees: Array<{ id: string; name: string }>; assigneesTruncated?: boolean;
}
export interface AiReportReviewUpdate {
  expectedVersion: number; status: AiReviewStatus; category: AiReviewCategory | null;
  assignedUserId: string | null; dueAt: string | null; resolutionNote: string;
}
export interface AiReportingReadiness {
  status: 'ready' | 'disabled' | 'checking' | 'needs_attention';
  ready: boolean;
  checks: Array<{ id: string; label: string; passed: boolean; message: string }>;
  message: string;
  checkedAt: string | null;
  sample: { question: string; label: string; value: number | null; currency?: string; dataType?: string; scopeLabel: string } | null;
  scope: AiSetupScope;
  model: string;
}

export const aiReportingService = {
  async declareRendered(requestId: string, surface: 'workspace' | 'assistant', signal?: AbortSignal): Promise<{ recorded: boolean }> {
    const { data } = await apiClient.post(`/ai-reporting/receipts/${encodeURIComponent(requestId)}`, { surface }, { signal, timeout: 5000 });
    return data;
  },
  async workItems(state: string, cursor?: string): Promise<{ items: AiReportWorkItem[]; hasMore: boolean; nextCursor: string | null }> {
    const { data } = await apiClient.get('/ai-reporting/work-items', { params: { state, cursor } }); return data;
  },
  async workPreview(requestId: string, entityChoice?: { sessionId: string; choiceId: string }, insightId?: string): Promise<AiReportWorkPreview> {
    if (insightId) { const { data } = await apiClient.get(`/ai-insights/${encodeURIComponent(insightId)}/work-items/source`); return data; }
    const { data } = await apiClient.get(`/ai-reporting/work-items/source/${encodeURIComponent(requestId)}`, entityChoice ? { params: entityChoice } : undefined); return data;
  },
  async reviewWorkEntity(id: string, signal?: AbortSignal): Promise<AiReportResponse> {
    const { data } = await apiClient.post(`/ai-reporting/work-items/${encodeURIComponent(id)}/report`, {}, { signal, timeout: AI_REPORTING_REQUEST_TIMEOUT_MS }); return data;
  },
  async workDetail(id: string): Promise<AiReportWorkDetail> {
    const { data } = await apiClient.get(`/ai-reporting/work-items/${encodeURIComponent(id)}`); return data;
  },
  async createWork(request: AiReportWorkCreate): Promise<AiReportWorkDetail> {
    if (request.insightId) {
      const { insightId, requestId: _requestId, entityChoice: _entityChoice, ...body } = request;
      const { data } = await apiClient.post(`/ai-insights/${encodeURIComponent(insightId)}/work-items`, body); return data;
    }
    const { data } = await apiClient.post('/ai-reporting/work-items', request); return data;
  },
  async updateWork(id: string, request: AiReportWorkUpdate): Promise<AiReportWorkDetail> {
    const { data } = await apiClient.patch(`/ai-reporting/work-items/${encodeURIComponent(id)}`, request); return data;
  },
  async createEntityBatch(items: AiReportWorkCreate[], signal?: AbortSignal): Promise<AiReportEntityBatchResult> {
    const { data } = await apiClient.post('/ai-reporting/work-items/entity-batch', { items }, { timeout: 60000, signal }); return data;
  },
  async definitions(): Promise<{ definitions: AiMetricDefinition[]; checkedAt: string; approvalMeaning: string }> {
    const { data } = await apiClient.get('/ai-reporting/definitions');
    return data;
  },
  async definitionDetail(metricId: string): Promise<AiMetricDefinitionDetail> {
    const { data } = await apiClient.get(`/ai-reporting/definitions/${encodeURIComponent(metricId)}`);
    return data;
  },
  async updateDefinition(metricId: string, request: AiMetricDefinitionUpdate): Promise<{ saved: boolean; version: number }> {
    const { data } = await apiClient.patch(`/ai-reporting/definitions/${encodeURIComponent(metricId)}`, request);
    return data;
  },
  async activity(days: 7 | 30): Promise<AiReportActivity> {
    const { data } = await apiClient.get('/ai-reporting/activity', { params: { days } });
    return data;
  },
  async reviews(status = 'open', cursor?: string, rating = 'all'): Promise<{ cases: AiReportReviewCase[]; hasMore: boolean; nextCursor: string | null }> {
    const { data } = await apiClient.get('/ai-reporting/reviews', { params: { status, limit: 50, cursor, rating } });
    return data;
  },
  async reviewDetail(caseId: string): Promise<AiReportReviewDetail> {
    const { data } = await apiClient.get(`/ai-reporting/reviews/${encodeURIComponent(caseId)}`);
    return data;
  },
  async updateReview(caseId: string, request: AiReportReviewUpdate): Promise<{ saved: boolean; version: number }> {
    const { data } = await apiClient.patch(`/ai-reporting/reviews/${encodeURIComponent(caseId)}`, request);
    return data;
  },
  async runTemplate(request: { templateId: string; companyId?: number; branchIds?: string[]; signal?: AbortSignal }): Promise<AiReportResponse> {
    const { templateId, signal, ...scope } = request;
    const { data } = await apiClient.post<AiReportResponse>(`/ai-reporting/templates/${templateId}/run`, scope, { signal, timeout: AI_REPORTING_REQUEST_TIMEOUT_MS });
    return data;
  },
  async interact(request: AiReportInteractionRequest): Promise<AiReportResponse> {
    const { sessionId, signal, ...body } = request;
    const { data } = await apiClient.post<AiReportResponse>(`/ai-reporting/interactions/${sessionId}`, body, { signal, timeout: AI_REPORTING_REQUEST_TIMEOUT_MS });
    return data;
  },

  async restore(requestId: string, signal?: AbortSignal): Promise<AiReportResponse> {
    const { data } = await apiClient.post<AiReportResponse>(`/ai-reporting/history/${requestId}/restore`, {}, { signal, timeout: AI_REPORTING_REQUEST_TIMEOUT_MS });
    return data;
  },

  async query(request: AiReportRequest): Promise<AiReportResponse> {
    const { signal, ...body } = request;
    const { data } = await apiClient.post<AiReportResponse>('/ai-reporting/query', body, {
      signal,
      timeout: AI_REPORTING_REQUEST_TIMEOUT_MS,
    });
    return data;
  },

  async dashboard(request: AiReportRequest): Promise<AiReportResponse> {
    const { signal, ...body } = request;
    const { data } = await apiClient.post<AiReportResponse>('/ai-reporting/dashboard', body, {
      signal,
      timeout: AI_REPORTING_REQUEST_TIMEOUT_MS,
    });
    return data;
  },

  async catalog(): Promise<AiCatalogMetadata> {
    const { data } = await apiClient.get<AiCatalogMetadata>('/ai-reporting/catalog');
    return data;
  },

  async feedback(requestId: string, rating: 'helpful' | 'incorrect' | 'unclear', comment?: string): Promise<void> {
    await apiClient.post(`/ai-reporting/query/${encodeURIComponent(requestId)}/feedback`, { rating, comment });
  },

  async history(limit = 25): Promise<AiReportHistoryItem[]> {
    const { data } = await apiClient.get<AiReportHistoryItem[]>('/ai-reporting/history', { params: { limit } });
    return data;
  },

  async settings(): Promise<AiProviderSettings> {
    const { data } = await apiClient.get<AiProviderSettings>('/ai-reporting/settings');
    return data;
  },

  async updateSettings(request: AiProviderSettingsUpdate): Promise<AiProviderSettings> {
    const { data } = await apiClient.patch<AiProviderSettings>('/ai-reporting/settings', request);
    return data;
  },

  async testSettings(): Promise<AiProviderTestResult> {
    const { data } = await apiClient.post<AiProviderTestResult>('/ai-reporting/settings/test');
    return data;
  },

  async usage(): Promise<AiTenantUsageSummary> {
    const { data } = await apiClient.get<AiTenantUsageSummary>('/ai-reporting/usage');
    return data;
  },

  async readiness(scope: AiSetupScope = {}): Promise<AiReportingReadiness> {
    const { data } = await apiClient.get<AiReportingReadiness>('/ai-reporting/settings/readiness', { params: scope });
    return data;
  },

  async verifySetup(scope: AiSetupScope = {}): Promise<AiReportingReadiness> {
    const { data } = await apiClient.post<AiReportingReadiness>('/ai-reporting/settings/verify', scope, { timeout: AI_REPORTING_REQUEST_TIMEOUT_MS });
    return data;
  },
};
